import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { fetchTeamInfo } from '../../actions/TeamInfo.actions';

// Hooks
import { useWorkspace } from './use-workspace';
import { useToggle } from 'Shared/utils/hooks';
import { useFetchBulkBAData } from './use-fetch-bulk-ba-data';

// Types
import { IndividualContributionInsight, Member } from 'Shared/types/TeamInfo.types';

// Selectors
import {
  getTeamInfoByWorkspaceSlug,
  getTeamInfoLoading,
  getTeamInfoError,
  getTeamType,
  getShortTeamDescription,
  getTeamConversationTips,
  getTeamReminders,
  getIndividualContributionInsight,
} from '../../selectors/TeamInfo.selectors';
import { getAllBaInfo } from 'Shared/selectors/BaInfo.selectors';

export const useTeamInfo = () => {
  const dispatch = useDispatch();
  const { users, workspace } = useWorkspace();
  const [isFetching, toggleIsFetching] = useToggle(false);

  // Get BA data for all users
  const allBaInfo = useSelector(getAllBaInfo);
  const { isLoading: isLoadingBA, usersWithBA } = useFetchBulkBAData(users);

  // Use selectors to get team info data
  const cachedTeamInfo = useSelector(state => getTeamInfoByWorkspaceSlug(state, { workspaceSlug: workspace.id }));
  const loading = useSelector(getTeamInfoLoading);
  const error = useSelector(getTeamInfoError);

  // Get specific team info
  const teamType = useSelector(state => getTeamType(state, { workspaceSlug: workspace.id }));
  const shortTeamDescription = useSelector(state => getShortTeamDescription(state, { workspaceSlug: workspace.id }));
  const teamConversationTips = useSelector(state => getTeamConversationTips(state, { workspaceSlug: workspace.id }));
  const teamReminders = useSelector(state => getTeamReminders(state, { workspaceSlug: workspace.id }));
  const individualContributionInsight: IndividualContributionInsight[] = useSelector(state =>
    getIndividualContributionInsight(state, { workspaceSlug: workspace.id })
  );

  useEffect(() => {
    // Skip if we're still loading BA data, have no users, or already have cached data
    if (isLoadingBA || !users || users.length === 0 || !workspace.id || cachedTeamInfo) {
      return;
    }

    toggleIsFetching();

    // Map users to members with self-pattern data from BA info
    const members: Member[] = users
      .filter(user => user.piUserGuid)
      .map(user => {
        // First check if this user is in the usersWithBA list
        const hasBA = usersWithBA.find(userWithBA => userWithBA.piUserGuid === user.piUserGuid);

        if (!hasBA) {
          return null;
        }

        // Get the actual BA data from the allBaInfo selector using piUserGuid as key
        const baInfo = allBaInfo.get(user.piUserGuid)?.toJS();

        // Extract sigma values with default values
        const selfPatternData = {
          a: baInfo?.selfAssessmentScore?.aSigma,
          b: baInfo?.selfAssessmentScore?.bSigma,
          c: baInfo?.selfAssessmentScore?.cSigma,
          d: baInfo?.selfAssessmentScore?.dSigma,
        };

        return {
          surveyUserGuid: user.piUserGuid,
          selfPatternData,
        };
      })
      .filter(Boolean); // Remove null entries

    if (members.length > 0) {
      const fetchData = async () => {
        try {
          await dispatch(fetchTeamInfo({ members }, workspace.id));
        } catch (error) {
          console.error('Error fetching team info:', error);
        } finally {
          toggleIsFetching();
        }
      };

      fetchData();
    } else {
      toggleIsFetching();
    }
  }, [users, isLoadingBA]);

  return {
    teamType,
    shortTeamDescription,
    teamConversationTips,
    teamReminders,
    individualContributionInsight,
    loading: loading || isLoadingBA || (!cachedTeamInfo && isFetching),
    error,
    isFetching,
  };
};
